import React from 'react';
import {
    TranscriptionExtended,
    TranscriptionExtendedResultCode,
} from 'flow/types/CallRecord';
import { CallRecordKey } from 'flow/types/EntityKeys';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { segmentTrack } from 'flow/actions/segment-actions';
import { TICKET_STRINGS } from 'flow/utils/segment-utils';
import { selectHasCapabilityLegacyTranscribeOnCall } from 'flow/selectors/own-extension-selectors';
import {
    selectHasExtendedTranscriptionInfo,
    selectCallRecordIsPersonalRecording,
    selectCallRecordRecordingPlaybackUrl,
    selectExtendedTranscriptionIsPersonal,
    selectCallRecordVoiceMessageUniqueId,
} from 'flow/selectors/call-records-selectors';
import { PageType } from 'flow/components/call-history-v2/types';
import ButtonGenerateTranscription from './ButtonGenerateTranscription';
import ButtonTranscriptionSkeleton from './ButtonTranscriptionSkeleton';
import CallRecordAudioPlayerV2 from 'flow/components/pbx/v2/misc/CallRecordAudioPlayerV2';
import CardCallDetailsContainer from 'flow/components/pbx/v2/misc/CardCallDetailsContainer';
import RecordingContextMenu from 'flow/components/pbx/v2/detail-page/call-event/RecordingContextMenu/RecordingContextMenu';
import TranscriptButton from './TranscriptButton';

const RETRYABLE_RESULT_CODES: TranscriptionExtendedResultCode[] = [
    'ERROR',
    'UNKNOWN',
    'QUOTA_EXCEEDED',
    'NO_CONTENT',
];

const resultCodeAllowsRetry = (code?: TranscriptionExtendedResultCode) =>
    !!code && RETRYABLE_RESULT_CODES.includes(code);

interface Props {
    callRecordKey: CallRecordKey;
    extendedTranscription?: TranscriptionExtended;
    transcriptionIsPending?: boolean;
    onAwaitTranscriptionConfirmation?: () => void;
    legacyTranscript?: string;
    legacySummary?: string;
    isGeneratingLegacyTranscription?: boolean;
    isAttemptingToFetchLegacyTranscription?: boolean;
    onLegacyTranscribe?: () => Promise<unknown>;
    hidePersonalContent?: boolean;
    pageType?: PageType;
}

const SectionRecordingControls = ({
    callRecordKey,
    extendedTranscription,
    transcriptionIsPending,
    onAwaitTranscriptionConfirmation,
    legacyTranscript,
    legacySummary,
    isGeneratingLegacyTranscription,
    isAttemptingToFetchLegacyTranscription,
    onLegacyTranscribe,
    hidePersonalContent,
    pageType,
}: Props) => {
    const dispatch = useAppDispatch();

    // These four events have only ever been reported for the ticket feed — they came from the
    // per-fragment ticket components these shared controls replaced. Emitting them from the call
    // record page too would add volume the existing dashboards don't expect, so the gate lives here
    // and the controls below take plain click callbacks, knowing nothing about tickets or Segment.
    const trackTicketOnly = (
        event: string,
        properties: Record<string, unknown>,
    ) => {
        if (pageType !== 'tickets') return;
        dispatch(segmentTrack(event, properties));
    };
    const hasCapabilityLegacyTranscribeOnCall = useAppSelector(
        selectHasCapabilityLegacyTranscribeOnCall,
    );
    const playbackUrl = useAppSelector((state) =>
        selectCallRecordRecordingPlaybackUrl(state, callRecordKey),
    );
    const voiceMessageUniqueId = useAppSelector((state) =>
        selectCallRecordVoiceMessageUniqueId(state, callRecordKey),
    );
    const recordHasExtendedTranscriptionInfo = useAppSelector((state) =>
        selectHasExtendedTranscriptionInfo(state, callRecordKey),
    );
    const isPersonalRecording = useAppSelector((state) =>
        selectCallRecordIsPersonalRecording(state, callRecordKey),
    );
    const transcriptionIsPersonal = useAppSelector((state) =>
        selectExtendedTranscriptionIsPersonal(state, callRecordKey),
    );

    const recordingIsHidden = hidePersonalContent && isPersonalRecording;
    const transcriptionIsHidden =
        hidePersonalContent && transcriptionIsPersonal;

    const shouldDisplayRecording =
        !recordingIsHidden && (!!playbackUrl || !!voiceMessageUniqueId);

    // The context menu can hold a "delete transcription" action even when
    // there's no audio at all (e.g. the audio was deleted server-side while
    // the transcription remains). Allow the menu to render in that case.
    const hasDeletableTranscription =
        !transcriptionIsHidden && transcriptionIsPersonal;

    const hasExtendedTranscriptionInfo =
        !transcriptionIsHidden && recordHasExtendedTranscriptionInfo;
    // Show "Show full transcript" button when transcription isn't hidden, and:
    // - extendedTranscriptionInfo exists and result is not a retryable error
    //   (optimistic: shows transcript button while extended transcription is being fetched), *or*
    // - a legacy transcription with content has been found
    const showTranscriptButton =
        !transcriptionIsHidden &&
        ((hasExtendedTranscriptionInfo &&
            !resultCodeAllowsRetry(extendedTranscription?.resultCode)) ||
            !!legacyTranscript);

    if (!shouldDisplayRecording && !hasExtendedTranscriptionInfo) return null;

    const renderTranscriptionButton = () => {
        if (transcriptionIsHidden) return null;

        if (
            isAttemptingToFetchLegacyTranscription &&
            hasCapabilityLegacyTranscribeOnCall
        ) {
            // Without the legacy transcribe-on-call capability we can expect there to be no
            // legacy transcription. Rendering nothing up front is preferable to flashing a
            // skeleton that will most likely transition into displaying nothing.
            return <ButtonTranscriptionSkeleton />;
        }

        if (showTranscriptButton) {
            return (
                <TranscriptButton
                    callRecordKey={callRecordKey}
                    extendedTranscription={extendedTranscription}
                    plainTranscript={legacyTranscript}
                    plainSummary={legacySummary}
                    isGenerating={transcriptionIsPending}
                    pageType={pageType}
                    onClickShowTranscript={() =>
                        trackTicketOnly(
                            TICKET_STRINGS.VIEW_FULL_TRANSCRIPT_CLICK,
                            { component: 'Event Item Call Recording' },
                        )
                    }
                />
            );
        }

        return (
            <ButtonGenerateTranscription
                callRecordKey={callRecordKey}
                transcriptionIsPending={transcriptionIsPending}
                onAwaitTranscriptionConfirmation={
                    onAwaitTranscriptionConfirmation
                }
                isGeneratingLegacyTranscription={
                    isGeneratingLegacyTranscription
                }
                onLegacyTranscribe={onLegacyTranscribe}
                onClickGenerate={() =>
                    trackTicketOnly(TICKET_STRINGS.GENERATE_TRANSCRIPT, {
                        // Deliberately not 'Event Item Call Recording' like the others: this event
                        // has always reported a different component.
                        component: 'Ticket Event Call Recording',
                    })
                }
            />
        );
    };

    return (
        <CardCallDetailsContainer className="items-center gap-1">
            {shouldDisplayRecording && (
                <CallRecordAudioPlayerV2
                    callRecordKey={callRecordKey}
                    // The call record page has one player and may fetch its
                    // recording ahead of Play; a ticket feed can hold many
                    // calls. A voicemail never loads before Play.
                    preload={pageType === 'tickets' ? 'none' : 'metadata'}
                    onClickPlayPause={(value) =>
                        trackTicketOnly(
                            TICKET_STRINGS.PLAY_CALL_RECORDING_CLICK,
                            { component: 'Event Item Call Recording', value },
                        )
                    }
                />
            )}
            {renderTranscriptionButton()}
            {(shouldDisplayRecording || hasDeletableTranscription) && (
                <RecordingContextMenu
                    callRecordKey={callRecordKey}
                    recordingIsHidden={recordingIsHidden}
                    transcriptionIsHidden={transcriptionIsHidden}
                    onClickDownloadRecording={() =>
                        trackTicketOnly(
                            TICKET_STRINGS.DOWNLOAD_CALL_RECORDING_CLICK,
                            { component: 'Event Item Call Recording' },
                        )
                    }
                />
            )}
        </CardCallDetailsContainer>
    );
};

export default SectionRecordingControls;
